import { DialogComponent } from '@theme/dialog';

const STORAGE_PREFIX = 'newsletter-popup:';
const EDITOR_PREFIX = 'newsletter-popup-editor:';

/**
 * Reads the stored state for a popup.
 *
 * @param {string} key - The storage key.
 * @returns {{ dismissedAt?: number, subscribed?: boolean }} The stored state, or an empty object.
 */
function readState(key) {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_PREFIX + key) ?? '{}');
  } catch {
    return {};
  }
}

/**
 * Merges values into the stored state for a popup.
 *
 * @param {string} key - The storage key.
 * @param {{ dismissedAt?: number, subscribed?: boolean }} values - The values to store.
 */
function writeState(key, values) {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify({ ...readState(key), ...values }));
  } catch {
    // Storage is unavailable (private browsing, quota). The popup still works for this page view.
  }
}

/**
 * A newsletter popup that opens itself after a delay, and collapses into a floating button when
 * dismissed. Once dismissed it stays closed for a configurable number of hours — the floating
 * button is the only way back in during that window.
 *
 * @typedef {object} NewsletterPopupRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} [floatingButton] - The button shown once the popup is dismissed.
 *
 * @extends DialogComponent
 */
class NewsletterPopup extends DialogComponent {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** Set while the theme editor is closing the popup because the section was deselected. */
  #deselecting = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('dialog:open', this.#handleOpen);
    this.addEventListener('dialog:close', this.#handleClose);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
      document.addEventListener('shopify:block:select', this.#handleSectionSelect);

      // Editing a setting re-renders the section, which replaces this element. Reopen so the
      // merchant keeps seeing what they are editing instead of the popup vanishing on every change.
      // With the popup closed, show the collapsed state instead — otherwise the floating button
      // comes back `hidden` on every re-render and the button settings look like they do nothing.
      if (this.#editorOpen) this.showDialog();
      else this.#toggleFloatingButton(true);
      return;
    }

    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    clearTimeout(this.#timer);
    this.removeEventListener('dialog:open', this.#handleOpen);
    this.removeEventListener('dialog:close', this.#handleClose);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
    document.removeEventListener('shopify:block:select', this.#handleSectionSelect);
    this.#releaseScrollLock();
  }

  /**
   * Undoes the scroll lock `showDialog` applies to the body. Closing the dialog normally handles
   * this, but a theme editor re-render tears the element out while it is still open, which would
   * otherwise leave the page frozen.
   */
  #releaseScrollLock() {
    if (!this.refs.dialog?.open) return;

    const { top } = document.body.style;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    if (top) window.scrollTo({ top: -parseInt(top, 10), behavior: 'instant' });
  }

  /** @returns {string} The key this popup stores its state under. */
  get #storageKey() {
    return this.dataset.sectionId ?? 'default';
  }

  /** @returns {number} How long to wait before the first automatic display, in milliseconds. */
  get #delay() {
    return Number(this.dataset.delay ?? 0) * 1000;
  }

  /** @returns {number} How long a dismissal suppresses the automatic display, in milliseconds. */
  get #dismissDuration() {
    return Number(this.dataset.dismissHours ?? 48) * 60 * 60 * 1000;
  }

  /** @returns {boolean} Whether the popup was open before the theme editor re-rendered it. */
  get #editorOpen() {
    try {
      return sessionStorage.getItem(EDITOR_PREFIX + this.#storageKey) === 'true';
    } catch {
      return false;
    }
  }

  /** @param {boolean} open - Whether the popup is open, so a re-render can restore it. */
  #setEditorOpen(open) {
    try {
      if (open) sessionStorage.setItem(EDITOR_PREFIX + this.#storageKey, 'true');
      else sessionStorage.removeItem(EDITOR_PREFIX + this.#storageKey);
    } catch {
      // Storage is unavailable; the popup just will not survive a re-render.
    }
  }

  /**
   * Decides what to do on page load: show the thank you message, wait to open, or offer the
   * floating button because the popup was already dismissed.
   */
  #schedule() {
    const state = readState(this.#storageKey);

    // The customer form posts back to the same page, so reopen to show the success message.
    if (this.querySelector('.email-signup__message--success')) {
      writeState(this.#storageKey, { subscribed: true });
      this.showDialog();
      return;
    }

    if (state.subscribed) return;

    const dismissedAt = state.dismissedAt ?? 0;

    if (Date.now() - dismissedAt < this.#dismissDuration) {
      this.#toggleFloatingButton(true);
      return;
    }

    this.#timer = setTimeout(() => this.showDialog(), this.#delay);
  }

  /**
   * @param {boolean} visible - Whether the floating button should be shown.
   */
  #toggleFloatingButton(visible) {
    const { floatingButton } = this.refs;

    if (!floatingButton) return;

    floatingButton.hidden = !visible;
  }

  #handleOpen = () => {
    this.#toggleFloatingButton(false);

    if (window.Shopify?.designMode) this.#setEditorOpen(true);
  };

  #handleClose = () => {
    if (window.Shopify?.designMode) {
      this.#setEditorOpen(false);

      // Deselecting the section is not a dismissal, so leave the floating button hidden. Closing it
      // by hand is, and previewing the collapsed state is the only way to style that button.
      this.#toggleFloatingButton(!this.#deselecting);
      this.#deselecting = false;
      return;
    }

    writeState(this.#storageKey, { dismissedAt: Date.now() });
    this.#toggleFloatingButton(true);
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (!(event instanceof CustomEvent) || !this.#ownsEvent(event)) return;

    this.#deselecting = false;
    this.showDialog();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (!(event instanceof CustomEvent) || !this.#ownsEvent(event)) return;

    this.#deselecting = true;
    this.closeDialog();
  };

  /**
   * Checks whether a theme editor event targets this popup.
   *
   * @param {CustomEvent} event - The theme editor event.
   * @returns {boolean} True when the event belongs to this section.
   */
  #ownsEvent(event) {
    const { target } = event;

    if (!(target instanceof Node)) return false;

    // Selecting the section targets the wrapper around this popup; selecting a block inside it
    // targets one of its descendants.
    return target.contains(this) || this.contains(target);
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
